import { compactParams } from './utils/object-utils.js'
import { truncateString } from './utils/string-utils.js'

/**
 * Analytics module for promo web applications.
 *
 * Goals:
 * - Framework-agnostic usage (Polymer, plain JS, future React)
 * - Stable event taxonomy for GA4 / Firebase Analytics
 * - Cookie-free compatible session handling
 * - Screen tracking with previous screen and duration
 * - API telemetry support through external request services
 *
 * Notes:
 * - This module does not import Firebase, Bugsnag or any other vendor SDK directly.
 * - Integrations are implemented through external helpers.
 * - All comments are intentionally written in English.
 */

/**
 * @typedef {Object} AnalyticsAdapter
 * @property {(eventName: string, params?: Object) => void} [track] Handles analytics events and may forward them to GA4, Bugsnag or any other integration.
 * @property {(user: { id: string, loyalty_id: string }) => void} [setUser] Handles user identity propagation for connected integrations.
 */

/**
 * @typedef {Object} AnalyticsOptions
 * @property {AnalyticsAdapter[]} [adapters] Integration adapters.
 * @property {string} [lang] Current app language.
 * @property {string} [environment] Current environment, for example dev, stage or prod.
 * @property {string} [appVersion] Current app version.
 * @property {number} [slowApiThresholdMs=1000] Slow API threshold.
 * @property {number} [criticalApiThresholdMs=2000] Critical API threshold.
 * @property {() => string} [createSessionId] Custom session id factory.
 * @property {() => number} [now] Custom time provider.
 */

/**
 * @typedef {Object.<string, any>} AnalyticsParams
 */

/**
 * Safely creates a random runtime session id without depending on cookies.
 *
 * @returns {string}
 */
function defaultCreateSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  return `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

/**
 * Returns current timestamp.
 *
 * @returns {number}
 */
function defaultNow() {
  return Date.now()
}

/**
 * Extracts endpoint pathname from a URL-like string when needed by custom code.
 *
 * This helper is intentionally exported, but it is not applied automatically.
 * The analytics module stores endpoint values exactly as they are passed in.
 *
 * @param {string} input
 * @returns {string}
 */
function normalizeEndpoint(input) {
  if (!input) {
    return 'unknown'
  }

  try {
    const base =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost'
    const url = new URL(input, base)
    return url.pathname || String(input)
  } catch (error) {
    return String(input)
  }
}

/**
 * Analytics service.
 */
class Analytics {
  /**
   * @param {AnalyticsOptions} [options]
   */
  constructor({
    adapters = [],
    lang,
    environment,
    appVersion,
    slowApiThresholdMs = 1000,
    criticalApiThresholdMs = 2000,
    createSessionId = defaultCreateSessionId,
    now = defaultNow,
  } = {}) {
    this.adapters = Array.isArray(adapters) ? adapters : []
    this.lang = lang
    this.environment = environment
    this.appVersion = appVersion

    this.slowApiThresholdMs = slowApiThresholdMs
    this.criticalApiThresholdMs = criticalApiThresholdMs

    this._createSessionId = createSessionId
    this._now = now

    this.sessionId = this._createSessionId()
    this.userId = undefined
    this.currentScreen = undefined
    this.previousScreen = undefined
    this.currentScreenStartedAt = undefined
  }

  /**
   * Updates mutable runtime context.
   *
   * @param {Object} context
   * @param {string} [context.lang]
   * @param {string} [context.environment]
   * @param {string} [context.appVersion]
   */
  setContext({ lang, environment, appVersion } = {}) {
    if (lang !== undefined) {
      this.lang = lang
    }

    if (environment !== undefined) {
      this.environment = environment
    }

    if (appVersion !== undefined) {
      this.appVersion = appVersion
    }
  }

  /**
   * Registers an adapter at runtime.
   *
   * @param {AnalyticsAdapter} adapter
   */
  addAdapter(adapter) {
    if (!adapter) {
      return
    }

    this.adapters.push(adapter)
  }

  /**
   * Sets known user identity and forwards it to all adapters.
   *
   * @param {string} userId
   */
  setUser(userId) {
    if (!userId) {
      return
    }

    this.userId = String(userId)

    const user = {
      id: this.userId,
      loyalty_id: this.userId,
    }

    for (const adapter of this.adapters) {
      adapter?.setUser?.(user)
    }
  }

  /**
   * Clears known user identity.
   */
  clearUser() {
    this.userId = undefined
  }

  /**
   * Returns the current runtime session id.
   *
   * @returns {string}
   */
  getSessionId() {
    return this.sessionId
  }

  /**
   * Sends a raw analytics event after enriching it with global context.
   *
   * @param {string} eventName
   * @param {AnalyticsParams} [params]
   */
  track(eventName, params = {}) {
    if (!eventName) {
      return
    }

    const payload = this._buildPayload(params)

    for (const adapter of this.adapters) {
      adapter?.track?.(eventName, payload)
    }
  }

  /**
   * Tracks app initialization start.
   *
   * @param {string} [entryPoint]
   */
  appStart(entryPoint) {
    this.track('app_start', {
      entry_point: entryPoint,
    })
  }

  /**
   * Tracks that the application is ready for interaction.
   */
  appReady() {
    this.track('app_ready')
  }

  /**
   * Tracks a visible screen and updates previous/current screen state.
   * Automatically emits screen_leave for the previous screen.
   *
   * @param {string} screenName
   * @param {AnalyticsParams} [params]
   */
  screenView(screenName, params = {}) {
    if (!screenName) {
      return
    }

    this._closeCurrentScreen(screenName)

    this.previousScreen = this.currentScreen
    this.currentScreen = String(screenName)
    this.currentScreenStartedAt = this._now()

    this.track('screen_view', {
      ...params,
      screen_name: this.currentScreen,
      previous_screen: this.previousScreen,
    })
  }

  /**
   * Tracks that a modal has been opened.
   *
   * @param {string} modalName
   * @param {AnalyticsParams} [params]
   */
  modalOpen(modalName, params = {}) {
    this.track('modal_open', {
      ...params,
      modal_name: modalName,
    })
  }

  /**
   * Tracks that a modal has been closed.
   *
   * @param {string} modalName
   * @param {AnalyticsParams} [params]
   */
  modalClose(modalName, params = {}) {
    this.track('modal_close', {
      ...params,
      modal_name: modalName,
    })
  }

  /**
   * Tracks tab selection change.
   *
   * @param {string} tabName
   * @param {AnalyticsParams} [params]
   */
  tabChange(tabName, params = {}) {
    this.track('tab_change', {
      ...params,
      tab_name: tabName,
    })
  }

  /**
   * Tracks call-to-action click.
   *
   * @param {string} ctaName
   * @param {AnalyticsParams} [params]
   */
  ctaClick(ctaName, params = {}) {
    this.track('cta_click', {
      ...params,
      cta_name: ctaName,
    })
  }

  /**
   * Tracks first meaningful interaction with a form.
   *
   * @param {string} formName
   * @param {AnalyticsParams} [params]
   */
  formStart(formName, params = {}) {
    this.track('form_start', {
      ...params,
      form_name: formName,
    })
  }

  /**
   * Tracks form submission.
   *
   * @param {string} formName
   * @param {AnalyticsParams} [params]
   */
  formSubmit(formName, params = {}) {
    this.track('form_submit', {
      ...params,
      form_name: formName,
    })
  }

  /**
   * Tracks flow start.
   *
   * @param {string} flowName
   * @param {AnalyticsParams} [params]
   */
  flowStart(flowName, params = {}) {
    this.track('flow_start', {
      ...params,
      flow_name: flowName,
    })
  }

  /**
   * Tracks step visibility within a flow.
   *
   * @param {string} flowName
   * @param {string} stepName
   * @param {AnalyticsParams} [params]
   */
  stepView(flowName, stepName, params = {}) {
    this.track('step_view', {
      ...params,
      flow_name: flowName,
      step_name: stepName,
    })
  }

  /**
   * Tracks step completion within a flow.
   *
   * @param {string} flowName
   * @param {string} stepName
   * @param {AnalyticsParams} [params]
   */
  stepComplete(flowName, stepName, params = {}) {
    this.track('step_complete', {
      ...params,
      flow_name: flowName,
      step_name: stepName,
    })
  }

  /**
   * Tracks flow completion.
   *
   * @param {string} flowName
   * @param {AnalyticsParams} [params]
   */
  flowComplete(flowName, params = {}) {
    this.track('flow_complete', {
      ...params,
      flow_name: flowName,
    })
  }

  /**
   * Tracks successful login.
   *
   * @param {string} method
   * @param {AnalyticsParams} [params]
   */
  loginSuccess(method, params = {}) {
    this.track('login_success', {
      ...params,
      method,
    })
  }

  /**
   * Tracks failed login.
   *
   * @param {string} method
   * @param {string} reason
   * @param {AnalyticsParams} [params]
   */
  loginFailed(method, reason, params = {}) {
    this.track('login_failed', {
      ...params,
      method,
      reason: truncateString(reason),
    })
  }

  /**
   * Tracks unexpected UI state.
   *
   * @param {string} message
   * @param {AnalyticsParams} [params]
   */
  uiError(message, params = {}) {
    this.track('ui_error', {
      ...params,
      message: truncateString(message),
    })
  }

  /**
   * Tracks unhandled JavaScript exception.
   *
   * Adapters decide themselves whether this event should also be forwarded
   * to Bugsnag, logs, or any other system.
   *
   * @param {Error|string} error
   * @param {AnalyticsParams} [params]
   */
  jsException(error, params = {}) {
    const message = error?.message ?? String(error)

    this.track('js_exception', {
      ...params,
      message: truncateString(message),
    })
  }

  /**
   * Tracks API error.
   *
   * Endpoint is stored exactly as passed in.
   * Any normalization should be handled outside the analytics core.
   *
   * @param {Object} details
   * @param {string} details.endpoint
   * @param {string} [details.method]
   * @param {number} [details.status]
   * @param {string} [details.message]
   * @param {number} [details.duration]
   * @param {AnalyticsParams} [details.extra]
   */
  apiError({ endpoint, method, status, message, duration, extra } = {}) {
    this.track('api_error', {
      ...extra,
      endpoint,
      method,
      status,
      message: truncateString(message),
      duration,
    })
  }

  /**
   * Tracks generic API request telemetry.
   *
   * Endpoint is stored exactly as passed in.
   *
   * @param {Object} details
   * @param {string} details.endpoint
   * @param {string} [details.method]
   * @param {number} [details.status]
   * @param {number} details.duration
   * @param {boolean} details.success
   * @param {AnalyticsParams} [details.extra]
   */
  apiRequest({ endpoint, method, status, duration, success, extra } = {}) {
    const severity = this.getRequestSeverity(duration)

    this.track('api_request', {
      ...extra,
      endpoint,
      method,
      status,
      duration,
      success,
      severity,
    })
  }

  /**
   * Tracks slow API requests.
   *
   * Requests above the critical threshold are marked with `severity = critical`.
   * Adapters decide what to do with that signal.
   *
   * @param {Object} details
   * @param {string} details.endpoint
   * @param {string} [details.method]
   * @param {number} [details.status]
   * @param {number} details.duration
   * @param {AnalyticsParams} [details.extra]
   */
  apiSlow({ endpoint, method, status, duration, extra } = {}) {
    const severity = this.getRequestSeverity(duration)

    if (severity === 'normal') {
      return
    }

    this.track('api_slow', {
      ...extra,
      endpoint,
      method,
      status,
      duration,
      severity,
    })
  }

  /**
   * Returns request severity based on configured latency thresholds.
   *
   * @param {number} duration
   * @returns {'normal'|'slow'|'critical'}
   */
  getRequestSeverity(duration) {
    if (typeof duration !== 'number' || Number.isNaN(duration)) {
      return 'normal'
    }

    if (duration >= this.criticalApiThresholdMs) {
      return 'critical'
    }

    if (duration >= this.slowApiThresholdMs) {
      return 'slow'
    }

    return 'normal'
  }

  /**
   * Emits screen_leave for the current screen if one is active.
   * This can be called explicitly on route change or before page unload.
   *
   * @param {string} [nextScreen]
   */
  flushCurrentScreen(nextScreen) {
    this._closeCurrentScreen(nextScreen)
  }

  /**
   * Subscribes to global unhandled error events.
   *
   * This helper is optional. If Bugsnag or another integration already handles
   * global runtime errors, this method may be unnecessary.
   *
   * @param {AbortSignal} [signal]
   */
  bindGlobalErrorHandlers(signal) {
    const onError = event => {
      const error = event?.error || event?.message || 'Unknown error'
      this.jsException(error, {
        source: 'window_error',
      })
    }

    const onUnhandledRejection = event => {
      const reason = event?.reason || 'Unhandled promise rejection'
      this.jsException(reason, {
        source: 'unhandled_rejection',
      })
    }

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('error', onError, { signal })
      window.addEventListener('unhandledrejection', onUnhandledRejection, {
        signal,
      })
    }
  }

  /**
   * Binds automatic screen flush on page hide / unload.
   *
   * This helper is optional and mostly useful when explicit route control is
   * not available or when a fallback is needed.
   *
   * @param {AbortSignal} [signal]
   */
  bindPageLifecycle(signal) {
    const onPageHide = () => {
      this.flushCurrentScreen()
    }

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('pagehide', onPageHide, { signal })
      window.addEventListener('beforeunload', onPageHide, { signal })
    }
  }

  /**
   * Builds final payload with global context.
   *
   * @private
   * @param {AnalyticsParams} params
   * @returns {AnalyticsParams}
   */
  _buildPayload(params = {}) {
    return compactParams({
      screen_name: this.currentScreen,
      previous_screen: this.previousScreen,
      session_id: this.sessionId,
      loyalty_id: this.userId,
      lang: this.lang,
      environment: this.environment,
      app_version: this.appVersion,
      ...params,
    })
  }

  /**
   * Closes the current screen and emits screen_leave.
   *
   * @private
   * @param {string} [nextScreen]
   */
  _closeCurrentScreen(nextScreen) {
    if (!this.currentScreen || !this.currentScreenStartedAt) {
      return
    }

    const duration = Math.max(0, this._now() - this.currentScreenStartedAt)

    this.track('screen_leave', {
      screen_name: this.currentScreen,
      duration,
      next_screen: nextScreen,
    })
  }
}

export default Analytics
export { Analytics, normalizeEndpoint, truncateString, compactParams }
